/* ============================================================
   JACKIE'S CUSTOM CRAFT — Shopify Theme JavaScript
   Version: 1.0 | June 2026
   Designer: Jackie's Custom Craft, Henderson NV
   ============================================================

   HOW TO USE IN SHOPIFY:
   1. Go to Online Store → Themes → Edit Code
   2. Open Assets → Add a new file → name it "jcc-theme.js"
   3. Paste this entire file in
   4. In your theme.liquid, just before </body>, add:
      {{ 'jcc-theme.js' | asset_url | script_tag }}

   WHAT THIS FILE DOES:
   - Starfield canvas animation (twinkling stars + shooting stars)
   - Sticky nav opacity on scroll
   - Mobile hamburger menu toggle
   - Scroll-triggered fade-in animations
   - Review card auto-scroll carousel
   - Smooth scroll for anchor links
   - Newsletter form success state
   ============================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────
     1. STARFIELD CANVAS
     Creates a fixed full-page canvas with twinkling stars
     and occasional shooting stars behind all content.
     The canvas element gets id="jcc-starfield" and is
     inserted as the first child of <body>.
  ────────────────────────────────────────────────────────── */
  function jccInitStarfield() {
    var canvas = document.createElement('canvas');
    canvas.id = 'jcc-starfield';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.insertBefore(canvas, document.body.firstChild);

    var ctx = canvas.getContext('2d');
    var stars = [];
    var shootingStars = [];
    var STAR_COUNT = 120;
    var animFrame;

    function resize() {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    function randomBetween(min, max) {
      return Math.random() * (max - min) + min;
    }

    /* Build the star pool */
    function buildStars() {
      stars = [];
      for (var i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x:         Math.random() * canvas.width,
          y:         Math.random() * canvas.height,
          radius:    randomBetween(0.3, 1.6),
          baseAlpha: randomBetween(0.20, 0.65),
          alpha:     0,
          delta:     randomBetween(0.003, 0.010),  /* twinkle speed */
          rising:    Math.random() > 0.5
        });
      }
    }

    /* Twinkle each star */
    function updateStars() {
      stars.forEach(function (s) {
        if (s.rising) {
          s.alpha += s.delta;
          if (s.alpha >= s.baseAlpha) { s.rising = false; }
        } else {
          s.alpha -= s.delta;
          if (s.alpha <= 0.05) { s.rising = true; }
        }
      });
    }

    function drawStars() {
      stars.forEach(function (s) {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        /* Pale lavender/white mix for star color */
        var r = Math.random() > 0.7 ? 200 : 255;
        var g = Math.random() > 0.7 ? 200 : 255;
        var b = 255;
        ctx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + s.alpha + ')';
        ctx.fill();
      });
    }

    /* Shooting stars */
    function maybeSpawnShootingStar() {
      /* Very rare — roughly once every 8 seconds on average */
      if (Math.random() < 0.0008 && shootingStars.length < 2) {
        shootingStars.push({
          x:     randomBetween(0, canvas.width * 0.6),
          y:     randomBetween(0, canvas.height * 0.4),
          len:   randomBetween(80, 160),
          speed: randomBetween(6, 14),
          alpha: 0.8,
          angle: Math.PI / 5
        });
      }
    }

    function updateShootingStars() {
      shootingStars = shootingStars.filter(function (ss) {
        ss.x     += Math.cos(ss.angle) * ss.speed;
        ss.y     += Math.sin(ss.angle) * ss.speed;
        ss.alpha -= 0.015;
        return ss.alpha > 0;
      });
    }

    function drawShootingStars() {
      shootingStars.forEach(function (ss) {
        ctx.save();
        ctx.globalAlpha = ss.alpha;
        var grad = ctx.createLinearGradient(
          ss.x, ss.y,
          ss.x - Math.cos(ss.angle) * ss.len,
          ss.y - Math.sin(ss.angle) * ss.len
        );
        grad.addColorStop(0, 'rgba(255,255,255,0.9)');
        grad.addColorStop(1, 'rgba(201,184,240,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth   = 1.5;
        ctx.beginPath();
        ctx.moveTo(ss.x, ss.y);
        ctx.lineTo(
          ss.x - Math.cos(ss.angle) * ss.len,
          ss.y - Math.sin(ss.angle) * ss.len
        );
        ctx.stroke();
        ctx.restore();
      });
    }

    /* Main render loop */
    function render() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      updateStars();
      drawStars();
      maybeSpawnShootingStar();
      updateShootingStars();
      drawShootingStars();
      animFrame = requestAnimationFrame(render);
    }

    /* Handle resize */
    window.addEventListener('resize', function () {
      cancelAnimationFrame(animFrame);
      resize();
      buildStars();
      animFrame = requestAnimationFrame(render);
    });

    resize();
    buildStars();
    render();
  }


  /* ──────────────────────────────────────────────────────────
     2. STICKY NAV — opacity increases on scroll
     Adds class "scrolled" to element with id="jcc-header"
     or class ".jcc-nav" when page scrolls past 60px.
  ────────────────────────────────────────────────────────── */
  function jccInitStickyNav() {
    var nav = document.getElementById('jcc-header') ||
              document.querySelector('.jcc-nav');
    if (!nav) return;

    var THRESHOLD = 60;

    function onScroll() {
      if (window.scrollY > THRESHOLD) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); /* run once on load */
  }


  /* ──────────────────────────────────────────────────────────
     3. MOBILE HAMBURGER MENU
     Toggle class "open" on element with class ".jcc-nav__mobile"
     when the toggle button ".jcc-nav__toggle" is clicked.
     The toggle button also gets an aria-expanded attribute.
  ────────────────────────────────────────────────────────── */
  function jccInitMobileMenu() {
    var toggle  = document.querySelector('.jcc-nav__toggle');
    var mobileMenu = document.querySelector('.jcc-nav__mobile');
    if (!toggle || !mobileMenu) return;

    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Toggle navigation menu');

    toggle.addEventListener('click', function () {
      var isOpen = mobileMenu.classList.toggle('open');
      toggle.setAttribute('aria-expanded', String(isOpen));
    });

    /* Close menu when a link is clicked */
    mobileMenu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        mobileMenu.classList.remove('open');
        toggle.setAttribute('aria-expanded', 'false');
      });
    });

    /* Close menu on outside click */
    document.addEventListener('click', function (e) {
      if (!toggle.contains(e.target) && !mobileMenu.contains(e.target)) {
        mobileMenu.classList.remove('open');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }


  /* ──────────────────────────────────────────────────────────
     4. SCROLL-TRIGGERED FADE-IN ANIMATIONS
     Any element with class "jcc-fade-in" will fade in
     when it enters the viewport. Add delay classes for
     staggered animations:
       .jcc-fade-in--delay-1  (100ms delay)
       .jcc-fade-in--delay-2  (200ms delay)
       .jcc-fade-in--delay-3  (300ms delay)
       .jcc-fade-in--delay-4  (400ms delay)

     Usage in Liquid:
       <div class="jcc-fade-in">Your content</div>
  ────────────────────────────────────────────────────────── */
  function jccInitFadeIn() {
    var elements = document.querySelectorAll('.jcc-fade-in');
    if (!elements.length) return;

    /* Fallback for browsers without IntersectionObserver */
    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('jcc-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('jcc-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }


  /* ──────────────────────────────────────────────────────────
     5. REVIEW CAROUSEL AUTO-SCROLL
     Applies to a container with class ".jcc-carousel".
     Wraps children in a track and auto-scrolls horizontally.
     Pauses on hover/touch.

     Usage in Liquid:
       <div class="jcc-carousel">
         <div class="jcc-review-card jcc-glass">...</div>
         <div class="jcc-review-card jcc-glass">...</div>
       </div>
  ────────────────────────────────────────────────────────── */
  function jccInitCarousel() {
    var carousels = document.querySelectorAll('.jcc-carousel');
    if (!carousels.length) return;

    carousels.forEach(function (carousel) {
      var isPaused = false;
      var scrollAmount = 0;
      var speed = 0.5; /* pixels per frame */

      /* Clone children for seamless loop */
      var children = Array.from(carousel.children);
      children.forEach(function (child) {
        var clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        carousel.appendChild(clone);
      });

      carousel.style.overflowX = 'auto';
      carousel.style.display   = 'flex';
      carousel.style.gap       = '24px';
      carousel.style.scrollBehavior = 'auto';
      carousel.style.msOverflowStyle = 'none';
      carousel.style.scrollbarWidth  = 'none';

      /* Pause on hover */
      carousel.addEventListener('mouseenter', function () { isPaused = true;  });
      carousel.addEventListener('mouseleave', function () { isPaused = false; });

      /* Pause on touch */
      carousel.addEventListener('touchstart', function () { isPaused = true;  }, { passive: true });
      carousel.addEventListener('touchend',   function () {
        setTimeout(function () { isPaused = false; }, 2000);
      }, { passive: true });

      function tick() {
        if (!isPaused) {
          scrollAmount += speed;
          /* Reset to start when halfway through (seamless loop) */
          if (scrollAmount >= carousel.scrollWidth / 2) {
            scrollAmount = 0;
          }
          carousel.scrollLeft = scrollAmount;
        }
        requestAnimationFrame(tick);
      }

      requestAnimationFrame(tick);
    });
  }


  /* ──────────────────────────────────────────────────────────
     6. SMOOTH SCROLL FOR ANCHOR LINKS
     All <a href="#..."> links will smoothly scroll to target.
     Shopify's theme.liquid often prevents this natively.
  ────────────────────────────────────────────────────────── */
  function jccInitSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        var target = document.querySelector(this.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        var navHeight = 70; /* matches .jcc-nav height */
        var top = target.getBoundingClientRect().top + window.scrollY - navHeight;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }


  /* ──────────────────────────────────────────────────────────
     7. NEWSLETTER FORM
     Handles success state for forms with class ".jcc-newsletter__form".
     Replace the submit handler body with your actual Klaviyo /
     Mailchimp / Shopify Email API call.

     Shopify Email / Klaviyo integration tip:
     Keep the form action pointing to your Klaviyo or
     Shopify Email endpoint. This JS enhances the UX after
     a successful fetch submission.
  ────────────────────────────────────────────────────────── */
  function jccInitNewsletter() {
    var forms = document.querySelectorAll('.jcc-newsletter__form');
    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var input   = form.querySelector('.jcc-newsletter__input');
        var btn     = form.querySelector('.jcc-btn');
        var email   = input ? input.value.trim() : '';

        if (!email || !email.includes('@')) {
          if (input) {
            input.style.borderColor = 'rgba(245, 100, 100, 0.60)';
            input.focus();
          }
          return;
        }

        /* ── Replace this block with your real API call ───────
           Example with Klaviyo:
           fetch('https://a.klaviyo.com/client/subscriptions/?company_id=YOUR_ID', {
             method: 'POST',
             headers: { 'content-type': 'application/json', 'revision': '2023-02-22' },
             body: JSON.stringify({ data: { type: 'subscription', attributes: {
               list_id: 'YOUR_LIST_ID',
               email: email
             }}})
           });
        ─────────────────────────────────────────────────────── */
        console.log('JCC Newsletter signup:', email);

        /* Success UI */
        var parent = form.parentElement;
        form.style.display = 'none';
        var msg = document.createElement('p');
        msg.textContent = '✦ You\'re in! Welcome to Jackie\'s circle.';
        msg.style.cssText = 'color:#f5e6a3;font-size:1.1rem;margin-top:16px;text-align:center;';
        parent.appendChild(msg);
      });
    });
  }


  /* ──────────────────────────────────────────────────────────
     8. ORBIT ANIMATION (About Me portrait)
     Adds animated star dots orbiting the .jcc-portrait element.
     Stars are created as absolutely-positioned spans.
  ────────────────────────────────────────────────────────── */
  function jccInitPortraitOrbit() {
    var portraits = document.querySelectorAll('.jcc-portrait');
    if (!portraits.length) return;

    portraits.forEach(function (portrait) {
      portrait.style.position = 'relative';

      var ORBIT_STARS = 8;
      for (var i = 0; i < ORBIT_STARS; i++) {
        var star = document.createElement('span');
        var angle     = (i / ORBIT_STARS) * 360;
        var radius    = portrait.offsetWidth / 2 + 14;
        var size      = Math.random() > 0.5 ? 4 : 3;
        var duration  = (12 + i * 2) + 's';
        var delay     = -(i * (12 / ORBIT_STARS)) + 's';

        star.style.cssText = [
          'position:absolute',
          'width:'  + size + 'px',
          'height:' + size + 'px',
          'border-radius:50%',
          'background:' + (i % 2 === 0 ? '#c9b8f0' : '#add8e6'),
          'top:50%',
          'left:50%',
          'transform-origin:' + (-radius) + 'px 0',
          'animation:jccOrbit ' + duration + ' linear ' + delay + ' infinite',
          'pointer-events:none',
          'z-index:4'
        ].join(';');

        portrait.appendChild(star);
      }

      /* Inject keyframes if not already present */
      if (!document.getElementById('jcc-orbit-style')) {
        var style = document.createElement('style');
        style.id = 'jcc-orbit-style';
        style.textContent = '@keyframes jccOrbit{from{transform:rotate(0deg) translateX(' +
          (portrait.offsetWidth / 2 + 14) + 'px) rotate(0deg)}' +
          'to{transform:rotate(360deg) translateX(' +
          (portrait.offsetWidth / 2 + 14) + 'px) rotate(-360deg)}}';
        document.head.appendChild(style);
      }
    });
  }


  /* ──────────────────────────────────────────────────────────
     9. ANNOUNCEMENT BAR COUNTDOWN (optional)
     If you add a <div class="jcc-countdown" data-end="2026-12-31">
     anywhere on the page, it displays a live countdown.
     Great for sale announcements.
  ────────────────────────────────────────────────────────── */
  function jccInitCountdown() {
    var countdowns = document.querySelectorAll('.jcc-countdown');
    if (!countdowns.length) return;

    countdowns.forEach(function (el) {
      var endDate = new Date(el.getAttribute('data-end'));
      if (isNaN(endDate)) return;

      function tick() {
        var now   = new Date();
        var diff  = endDate - now;
        if (diff <= 0) { el.textContent = 'Sale ended!'; return; }

        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000)  / 60000);
        var s = Math.floor((diff % 60000)    / 1000);

        el.textContent =
          d + 'd ' +
          String(h).padStart(2,'0') + 'h ' +
          String(m).padStart(2,'0') + 'm ' +
          String(s).padStart(2,'0') + 's';
      }

      tick();
      setInterval(tick, 1000);
    });
  }


  /* ──────────────────────────────────────────────────────────
     10. INIT — Run everything when DOM is ready
  ────────────────────────────────────────────────────────── */
  function jccInit() {
    jccInitStarfield();
    jccInitStickyNav();
    jccInitMobileMenu();
    jccInitFadeIn();
    jccInitCarousel();
    jccInitSmoothScroll();
    jccInitNewsletter();
    jccInitPortraitOrbit();
    jccInitCountdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', jccInit);
  } else {
    jccInit();
  }

})();

/* ============================================================
   END OF JCC THEME JS
   Questions? Jackie's Custom Craft | Henderson, NV
   ============================================================ */
